import { Media } from "@/features/push/domain/models/media";
import {
  PushPrice,
  PushStatus,
  PushTarget
} from "@/features/push/domain/models/push";
import { HttpGateway } from "@/features/push/infrastructure/services/http";
import { captureError } from "@/shared/services/sentry";

export type CreatePushParams = {
  targets: PushTarget[];
  medias: string[];
  caption: string;
  scheduledAt: string;
  mod: boolean;
  teaserId?: string;
  playlists?: string[];
  price?: PushPrice;
  discount?: {
    price: PushPrice;
    duration: number;
  };
  thirdPartyConsent?: boolean;
};

export type CreatePushResponse = {
  id: string;
  targets: PushTarget[];
  teaser: string;
  medias: Media[];
  caption: string;
  scheduledAt: string;
  publishedAt: string;
  price: PushPrice;
  discount: {
    price: PushPrice;
    duration: number;
  };
  playlists: string[];
  thirdPartyConsent?: boolean;
  status: PushStatus;
  stats: {
    turnover: number;
    purchase: number;
    views: number;
    recipientCount: number;
  };
};

export type GetPushByIdResponse = {
  id: string;
  targets: PushTarget[];
  teaser: Media;
  medias: Media[];
  caption: string;
  scheduledAt: Date;
  publishedAt: string;
  price: PushPrice;
  discount: {
    price: PushPrice;
    duration: number;
  };
  playlists: string[];
  thirdPartyConsent?: boolean;
  status: PushStatus;
  mod: boolean;
  stats: {
    turnover: number;
    purchase: number;
    views: number;
    recipientCount: number;
  };
};

export type UpdatePushParams = {
  targets?: PushTarget[];
  teaserId?: string | null;
  medias?: string[];
  caption?: string;
  scheduledAt?: string;
  price?: PushPrice;
  discount?: {
    price: PushPrice;
    duration: number;
  } | null;
  playlists?: string[];
  thirdPartyConsent?: boolean;
  mod?: boolean;
};

export type UpdatePushResponse = {
  id: string;
  targets: PushTarget[];
  teaser: string;
  medias: Media[];
  caption: string;
  scheduledAt: string;
  publishedAt: string;
  price: PushPrice;
  discount: {
    price: PushPrice;
    duration: number;
  };
  playlists: string[];
  thirdPartyConsent?: boolean;
  status: PushStatus;
  stats: {
    turnover: number;
    purchase: number;
    views: number;
    recipientCount: number;
  };
};

export interface GetCalendarRestrictionsResponse {
  ex_subscribers: Array<string>;
  interested: Array<string>;
}

export interface GetCalendarDatesResponse {
  dates: Array<string>;
}

export interface GetPushPricesResponse {
  ranges: {
    value: number;
    currency: string;
  }[];
}

export interface GetPushDiscountDurationsResponse {
  durations: number[];
}

export const pushDataSourceImplementation = (gateway: HttpGateway) => ({
  createPush: async <T>(params: CreatePushParams): Promise<T> => {
    try {
      return await gateway.post("/v2/pushes", params);
    } catch (e: any) {
      captureError(e, "push", "createPush");

      throw Error("Error while creating push", {
        cause: e
      });
    }
  },
  getPushList: async <T>(): Promise<T> => {
    try {
      return await gateway.get("/v2/pushes");
    } catch (e: any) {
      captureError(e, "push", "getPushList");

      throw Error("Error while fetching push list", {
        cause: e.cause
      });
    }
  },
  getPushById: async <T>(id: string): Promise<T> => {
    try {
      return await gateway.get(`/v2/pushes/${id}`);
    } catch (e: any) {
      captureError(e, "push", "getPushById");

      throw Error("Error while fetching push by id", {
        cause: e.cause
      });
    }
  },
  updatePush: async <T>({
    id,
    params
  }: {
    id: string;
    params: UpdatePushParams;
  }): Promise<T> => {
    try {
      return await gateway.patch(`/v2/pushes/${id}`, params);
    } catch (e: any) {
      captureError(e, "push", "updatePush");

      throw Error("Error while updating push", {
        cause: e.cause
      });
    }
  },
  deletePush: async <T>(id: string): Promise<T> => {
    try {
      return await gateway.delete(`/v2/pushes/${id}`);
    } catch (e: any) {
      captureError(e, "push", "deletePush");

      throw Error("Error while deleting push", {
        cause: e.cause
      });
    }
  },
  getPushCalendarDates: async <T>(): Promise<T> => {
    try {
      return await gateway.get("/v2/pushes/calendar");
    } catch (e) {
      captureError(e, "push", "getPushCalendarDates");

      throw Error("Error while fetching push calendar dates", {
        cause: e
      });
    }
  },
  getPushCalendarRestrictions: async <T>(): Promise<T> => {
    try {
      return await gateway.get("/v2/pushes/calendar/restrictions");
    } catch (e) {
      captureError(e, "push", "getPushCalendarRestrictions");

      throw Error("Error while fetching push calendar restrictions", {
        cause: e
      });
    }
  },
  getPushPrices: async <T>(): Promise<T> => {
    try {
      return await gateway.get("/v2/pushes/prices");
    } catch (e) {
      captureError(e, "push", "getPushPrices");

      throw Error("Error while fetching push prices", {
        cause: e
      });
    }
  },
  getPushDiscountDurations: async <T>(): Promise<T> => {
    try {
      return await gateway.get("/v2/pushes/discount-durations");
    } catch (e) {
      captureError(e, "push", "getPushDiscountDurations");

      throw Error("Error while fetching push discount durations", {
        cause: e
      });
    }
  },
  getCalendarRestrictionsByPushId: async <T>(id: string): Promise<T> => {
    try {
      return await gateway.get(`/v2/pushes/${id}/calendar/restrictions`);
    } catch (e) {
      captureError(e, "push", "getCalendarRestrictionsByPushId");

      throw Error("Error while fetching calendar restrictions by push id", {
        cause: e
      });
    }
  }
});
